import { v4 as uuid } from 'uuid';

import {
  IS_PAY_ANYONE_P2P,
  IS_PAY_ANYONE_REFERRAL,
  IS_REFERRAL_SPECIFIC_ENROLLMENT,
} from 'consts/appConsts';
import { AJS_ANONYMOUS_ID } from 'consts/storageConsts';

import { AJS_ANONYMOUS_ID_QSP } from 'utils/queryParamHelpers';
import cookies from 'utils/storage/cookies';
import {
  getFromLocalStorage,
  setInLocalStorage,
} from 'utils/storage/storageHelper';
import stripDoubleQuotes from 'utils/stripDoubleQuotes';

/** Creates and stores a UUID anonymous ID - this function is generally
 * used in the case that Segment is blocked and cannot create the anon id. */
export function createAnonymousId() {
  const newAnonymousId = getCleanedAnonymousIdFromQSP() || uuid();

  storeAnonymousId(newAnonymousId);

  return newAnonymousId;
}

export function storeAnonymousId(newAnonymousId) {
  /** Store anonymous ID value in storage as we usually
   * only create our own anon ID when Segment is blocked
   * and cannot count on the Segment method below to do
   * the storing. */
  cookies.set(AJS_ANONYMOUS_ID, newAnonymousId);
  setInLocalStorage(AJS_ANONYMOUS_ID, newAnonymousId, 0);

  /** Try to set it through Segment as well in case Segment
   * finishes initializing after we created the anon ID */
  const { analytics } = window;

  if (analytics) {
    analytics.setAnonymousId(newAnonymousId);
  }
}

/** Returns the anonymous ID through Segments method if available.
 * if Segments blocked, returns anon ID stored in cookies/localStorage */
export function getAnonymousId() {
  const { analytics } = window;

  if (analytics && analytics.user) {
    const anonymousIdSegment = analytics.user().anonymousId();

    /** Confirm that Segment has a value stored before returning. */
    if (anonymousIdSegment) {
      return anonymousIdSegment;
    }
  }

  /** If Segment is blocked, we will have stored the anonymous ID in
   * cookies and localStorage. */
  const ajsAnonymousIdCookie = cookies.get(AJS_ANONYMOUS_ID);
  const ajsAnonymousIdLocalStorage = getCleanedAnonymousIdFromLocalStorage();

  return ajsAnonymousIdCookie || ajsAnonymousIdLocalStorage;
}

/**
 * Grabs ajs_anonymous_id value from localStorage and decodes + strips of double quotes
 */
export function getCleanedAnonymousIdFromLocalStorage() {
  const localStorageValue = decodeURIComponent(
    getFromLocalStorage(AJS_ANONYMOUS_ID)
  );

  // value could be "null" in some cases
  if (localStorageValue === 'null') {
    return '';
  }

  return stripDoubleQuotes(localStorageValue);
}

/** TODO: Remove the section below if possible once revising native
 * enrollment flow utils which use it (utils/nativeEnrollmentSession.js) */

/**
 * Returns true if "ajs_anonymous_id" cookie matches one from url
 *
 * @return {boolean} - values match
 */
export function anonymousIdCookieMatchQSP() {
  return cookies.get(AJS_ANONYMOUS_ID) === getCleanedAnonymousIdFromQSP();
}

/**
 * Grabs ajs_anonymous_id value from QSP and decodes + strips of double quotes
 */
export function getCleanedAnonymousIdFromQSP() {
  const qspValue = decodeURIComponent(AJS_ANONYMOUS_ID_QSP);

  if (qspValue === 'null') {
    return '';
  }

  return stripDoubleQuotes(qspValue);
}

export function getFlowName() {
  if (IS_PAY_ANYONE_REFERRAL || IS_PAY_ANYONE_P2P) {
    return 'PayAnyone';
  }

  if (IS_REFERRAL_SPECIFIC_ENROLLMENT) {
    return 'Referral';
  }

  return 'Regular';
}
